/**
 * Config mode store — admin-only flag that enables the "configure this
 * component" UX (CMD/Ctrl+click navigation, visual affordances, etc.).
 *
 * The store holds a single boolean (`enabled`) and three setters. Role
 * gating happens at the **UI / handler** layer via
 * {@link ../useIsConfigModeEligible useIsConfigModeEligible}; this store is
 * intentionally role-agnostic so it can be consumed from anywhere without
 * having to re-check permissions inline.
 *
 * Persistence is handled manually through `localStorage` under the key
 * `kusudama_config_mode_v1`:
 *
 * - Module initialisation reads the key and seeds the initial state.
 * - Every setter writes the new value back.
 * - Read and write errors are swallowed (private mode, quota exceeded,
 *   etc.) so the store always degrades to an in-memory session flag.
 *
 * We intentionally avoid `zustand/middleware`'s `persist` helper because no
 * other store in the project uses it — keeping the dependency surface flat
 * matches local convention (see `grep -R "zustand/middleware" src` — zero
 * hits) and lets us target this single boolean key without the overhead
 * of a hydration wrapper.
 *
 * @example
 * ```ts
 * import { useConfigMode } from '@components/shared/configurable';
 *
 * function HeaderBadge() {
 *   const { enabled, toggle } = useConfigMode();
 *   return <Button onClick={toggle}>{enabled ? 'ON' : 'OFF'}</Button>;
 * }
 * ```
 */

import { create } from 'zustand';

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/**
 * localStorage key used to persist the config-mode flag.
 *
 * The `_v1` suffix lets us evolve the persisted shape later without
 * breaking already-stored values — a future v2 can read both keys and
 * migrate.
 */
export const CONFIG_MODE_STORAGE_KEY = 'kusudama_config_mode_v1';

/**
 * Read the persisted value. Returns `false` if nothing is stored or the
 * read fails (storage unavailable, quota exceeded, corrupted value).
 *
 * Kept as a named helper so tests can spy on the single entry point.
 */
function readPersistedValue(): boolean {
  try {
    const raw = localStorage.getItem(CONFIG_MODE_STORAGE_KEY);
    if (raw === null) {
      return false;
    }
    return raw === 'true';
  } catch {
    // localStorage can throw in private/incognito modes or when disabled
    // by the user. We treat any failure as "off" — the feature is
    // admin-only anyway, so a session-scoped fallback is acceptable.
    return false;
  }
}

/**
 * Persist the flag. Failures are swallowed — see {@link readPersistedValue}.
 */
function writePersistedValue(enabled: boolean): void {
  try {
    localStorage.setItem(CONFIG_MODE_STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    // Swallow: see readPersistedValue() for rationale.
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Shape of the config-mode store. Intentionally minimal — adding UX state
 * (badge placement, last-viewed component, etc.) should happen in a
 * dedicated store so this one stays focused on the on/off switch.
 */
export interface ConfigModeState {
  /** Whether config mode is currently on. */
  enabled: boolean;
  /** Turn config mode on and persist the new value. */
  enable: () => void;
  /** Turn config mode off and persist the new value. */
  disable: () => void;
  /** Flip the current value and persist. */
  toggle: () => void;
}

/**
 * The raw Zustand store. Prefer {@link useConfigMode} at call sites that
 * only need to read/toggle the flag — it returns a stable object so
 * memoised consumers don't re-render on unrelated store slices.
 *
 * The store is exported for consumers that need selector-based access
 * (e.g., a header badge that only re-renders when `enabled` flips).
 */
export const useConfigModeStore = create<ConfigModeState>((set, get) => ({
  enabled: readPersistedValue(),
  enable: () => {
    writePersistedValue(true);
    set({ enabled: true });
  },
  disable: () => {
    writePersistedValue(false);
    set({ enabled: false });
  },
  toggle: () => {
    const next = !get().enabled;
    writePersistedValue(next);
    set({ enabled: next });
  },
}));

/**
 * Convenience hook returning the current flag and its setters.
 *
 * Role gating (is this user an admin?) happens **outside** this hook in
 * the click handler / keyboard shortcut / UI layer via
 * `useIsConfigModeEligible`. Keeping the store role-agnostic means:
 *
 * - Unit tests don't need to stub auth
 * - Non-admin codepaths can still read the flag without throwing
 * - The role check lives in one place that's easy to update
 *
 * @returns An object with `{ enabled, enable, disable, toggle }`.
 */
export function useConfigMode() {
  const enabled = useConfigModeStore((s) => s.enabled);
  const enable = useConfigModeStore((s) => s.enable);
  const disable = useConfigModeStore((s) => s.disable);
  const toggle = useConfigModeStore((s) => s.toggle);
  return { enabled, enable, disable, toggle };
}

// ---------------------------------------------------------------------------
// Test utilities
// ---------------------------------------------------------------------------

/**
 * Test-only reset helper.
 *
 * Zustand's `create` does not expose a built-in reset, so this helper
 * puts the store back into a known state (`enabled: false`) and clears
 * the persisted value. Tests call it from `beforeEach` to isolate store
 * state between cases.
 *
 * **Do not call from production code.** Wiping the flag mid-session
 * would defeat the persistence contract.
 */
export function __resetConfigModeStoreForTests(): void {
  try {
    localStorage.removeItem(CONFIG_MODE_STORAGE_KEY);
  } catch {
    // Swallow — see readPersistedValue().
  }
  useConfigModeStore.setState({ enabled: false });
}
